import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import Toast from '../components/Toast';
import Logo from '../components/Logo';

export default function VerifyEmail() {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' | 'warning' } | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser } = useAuth();
  
  const email = location.state?.email || '';

  // Debug: Email anzeigen
  console.log('VerifyEmail - Email from state:', email);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    console.log('Submitting verification:', { email, code, codeLength: code.length });
    
    if (!email) {
      setToast({ message: 'Email-Adresse fehlt. Bitte registriere dich erneut.', type: 'error' });
      return;
    }
    
    if (code.length !== 6) {
      setToast({ message: 'Code muss 6 Ziffern lang sein', type: 'error' });
      return;
    }

    setLoading(true);
    setToast(null);

    try {
      const payload = { email, code };
      console.log('Sending payload:', payload);
      const response = await api.post('/auth/verify-email', payload);
      
      // Save tokens
      localStorage.setItem('accessToken', response.data.data.accessToken);
      localStorage.setItem('refreshToken', response.data.data.refreshToken);
      localStorage.setItem('user', JSON.stringify(response.data.data.user));
      
      setUser(response.data.data.user);
      setToast({ message: 'Email erfolgreich verifiziert!', type: 'success' });
      
      setTimeout(() => navigate('/'), 1000);
    } catch (error: any) {
      console.error('Verification error:', error.response?.data);
      setToast({
        message: error.response?.data?.message || 'Verifizierung fehlgeschlagen',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const resendCode = async () => {
    setLoading(true);
    setToast(null);

    try {
      await api.post('/auth/resend-verification', { email });
      setToast({ message: 'Neuer Code wurde gesendet', type: 'success' });
    } catch (error: any) {
      setToast({
        message: error.response?.data?.message || 'Fehler beim Senden des Codes',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: 'linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%)',
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center', 
      padding: '1rem',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Decorative elements */}
      <div style={{
        position: 'absolute',
        top: '-50%',
        right: '-10%',
        width: '500px',
        height: '500px',
        borderRadius: '50%',
        background: 'rgba(255, 255, 255, 0.05)',
        filter: 'blur(60px)'
      }}></div>
      <div style={{
        position: 'absolute',
        bottom: '-30%',
        left: '-10%',
        width: '400px',
        height: '400px',
        borderRadius: '50%',
        background: 'rgba(255, 255, 255, 0.05)',
        filter: 'blur(60px)'
      }}></div>

      <div className="card" style={{ 
        width: '100%', 
        maxWidth: '480px', 
        padding: '3rem 2rem',
        position: 'relative',
        zIndex: 1,
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
      }}>
        <div style={{ textAlign: 'center', marginBottom: '2.5rem' }}>
          <div style={{ 
            width: '80px', 
            height: '80px', 
            margin: '0 auto 1.5rem',
            background: 'linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '2.5rem'
          }}>
            📧
          </div>
          <h1 style={{ fontSize: '1.75rem', fontWeight: 'bold', color: 'var(--text-primary)', marginBottom: '0.75rem' }}>
            Email bestätigen
          </h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: '1rem', lineHeight: '1.6' }}>
            Wir haben einen 6-stelligen Code an<br />
            <strong style={{ color: 'var(--primary)' }}>{email}</strong> gesendet
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '2rem' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '0.75rem', 
              fontWeight: '600',
              color: 'var(--text-primary)',
              fontSize: '0.875rem',
              textTransform: 'uppercase',
              letterSpacing: '0.05em'
            }}>
              Verifizierungscode
            </label>
            <input
              type="text"
              className="input"
              placeholder="• • • • • •"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              maxLength={6}
              style={{ 
                fontSize: '2rem', 
                letterSpacing: '1rem', 
                textAlign: 'center',
                fontWeight: 'bold',
                padding: '1.25rem',
                background: 'var(--bg-secondary)',
                border: '2px solid var(--border-color)'
              }}
              autoFocus
            />
          </div>

          <button
            type="submit"
            className="btn btn-primary"
            style={{ 
              width: '100%',
              padding: '1rem',
              fontSize: '1.125rem',
              fontWeight: '600'
            }}
            disabled={loading || code.length !== 6}
          >
            {loading ? (
              <>
                <span style={{ marginRight: '0.5rem' }}>⏳</span>
                Wird verifiziert...
              </>
            ) : (
              <>
                <span style={{ marginRight: '0.5rem' }}>✓</span>
                Email bestätigen
              </>
            )}
          </button>
        </form>

        <div style={{ 
          marginTop: '2rem', 
          paddingTop: '2rem',
          borderTop: '1px solid var(--border-color)',
          textAlign: 'center' 
        }}>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', marginBottom: '0.75rem' }}>
            Code nicht erhalten?
          </p>
          <button
            onClick={resendCode}
            disabled={loading}
            className="btn btn-secondary"
            style={{
              fontSize: '0.875rem',
              padding: '0.625rem 1.25rem'
            }}
          >
            📨 Code erneut senden
          </button>
        </div>
      </div>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}
